import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import {
    DndContext,
    closestCorners,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { SortableTaskCard, TaskCard } from './TaskCard';

const KanbanColumn = ({ status, tasks, children, onAdd, newTaskContent, setNewTaskContent, isPro, onRequestCreateTask }) => {
    // Prefix ID to avoid collision with task IDs
    const droppableId = `column-${status.id}`;

    // We pass data to help identify the container type
    const { setNodeRef } = useDroppable({
        id: droppableId,
        data: {
            type: 'Column',
            status,
        }
    });

    const isUncategorized = status.id === 'uncategorized';
    const statusName = isUncategorized ? 'Tasks' : status.name;
    const count = tasks.length;

    // Use passed color or defaults
    const headerClass = isUncategorized
        ? "p-3 border-bottom fw-bold d-flex justify-content-between text-muted"
        : `p-3 border-bottom fw-bold d-flex justify-content-between ${status.color || ''}`;

    return (
        <div ref={setNodeRef} className="kanban-column" style={{ backgroundColor: '#f8f9fa' }}>
            <div className={headerClass}>
                {statusName}
                <span className="badge bg-secondary rounded-pill">
                    {count}
                </span>
            </div>

            <div className="p-2 custom-scrollbar" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', minHeight: '100px' }}>
                <SortableContext
                    id={droppableId}
                    items={tasks.map(t => t.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <div className="d-flex flex-column gap-2">
                        {children}
                    </div>
                </SortableContext>
            </div>

            {/* Quick Add - Only for real statuses or if needed */}
            {!isUncategorized && (
                <div className="p-3 border-top">
                    {isPro ? (
                        <Button
                            variant="light"
                            className="w-100 text-start text-muted d-flex align-items-center gap-2"
                            onClick={() => onRequestCreateTask(status.id, statusName)}
                        >
                            <Plus size={16} />
                            Hinzufügen
                        </Button>
                    ) : (
                        <Form onSubmit={(e) => onAdd(e, status.id)}>
                            <div className="d-flex gap-1">
                                <Form.Control
                                    size="sm"
                                    placeholder="+ Task"
                                    value={newTaskContent[status.id] || ""}
                                    onChange={e => setNewTaskContent(p => ({ ...p, [status.id]: e.target.value }))}
                                />
                                {newTaskContent[status.id] && <Button size="sm" type="submit">Add</Button>}
                            </div>
                        </Form>
                    )}
                </div>
            )}
        </div>
    );
};

const KanbanBoard = ({ tasks, taskStatuses, reorderTasks, updateTask, createTask, createStatus, updateStatus, deleteStatus, projectId, onDeleteTask, allTags, onAssignTag, onRemoveTag, onCreateSubtask, onAddReminder, onDeleteReminder, isPro, onRequestCreateTask, members = [] }) => {
    const [activeId, setActiveId] = useState(null);
    const [newTaskContent, setNewTaskContent] = useState({}); // keyed by statusId
    const [isAddingStatus, setIsAddingStatus] = useState(false);
    const [newStatusName, setNewStatusName] = useState("");

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Group tasks by status
    const tasksByStatus = React.useMemo(() => {
        const groups = {};
        (taskStatuses || []).forEach(s => groups[s.id] = []);
        // Setup Uncategorized
        groups['uncategorized'] = [];

        (tasks || []).forEach(t => {
            if (t.status_id && groups[t.status_id]) {
                groups[t.status_id].push(t);
            } else {
                groups['uncategorized'].push(t);
            }
        });
        return groups;
    }, [tasks, taskStatuses]);

    // Dnd Handlers
    const handleDragStart = (event) => setActiveId(event.active.id);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        const activeTask = tasks.find(t => t.id === activeId);
        if (!activeTask) return;

        const activeStatusId = activeTask.status_id || 'uncategorized';

        // Find over status
        let overStatusId = null;
        let isOverColumn = false;

        // Check for column prefix
        const overIdString = String(overId);
        if (overIdString.startsWith('column-')) {
            isOverColumn = true;
            // Extract raw ID: "column-1" -> "1", "column-uncategorized" -> "uncategorized"
            const rawId = overIdString.replace('column-', '');

            // Validate it matches a known group
            if (rawId === 'uncategorized' || tasksByStatus[rawId] !== undefined) {
                overStatusId = rawId;
            }
        } else {
            // Dropped on a task
            const overTask = tasks.find(t => t.id === overId);
            if (overTask) {
                overStatusId = overTask.status_id || 'uncategorized';
            }
        }

        if (!overStatusId) return;

        // Determine correct targetStatusId for Data update
        let targetStatusIdForDb = null;
        if (overStatusId === 'uncategorized') {
            targetStatusIdForDb = null;
        } else {
            // Ensure we match type. Typically statuses have integer IDs.
            // If tasksByStatus keys are strings (from object coercion), we need to find keys.
            // The safest way is to find the status object.
            const status = taskStatuses.find(s => s.id == overStatusId); // loose helper for mix types
            if (status) targetStatusIdForDb = status.id;
            else targetStatusIdForDb = parseInt(overStatusId) || null; // Fallback
        }

        // Normalize IDs for comparison (handle null vs 'uncategorized', number vs string)
        const activeGroupKey = (activeStatusId === null || activeStatusId === 'uncategorized') ? 'uncategorized' : String(activeStatusId);
        const overGroupKey = (overStatusId === null || overStatusId === 'uncategorized') ? 'uncategorized' : String(overStatusId);

        if (activeGroupKey === overGroupKey) {
            // Reorder in same status
            const items = tasksByStatus[overGroupKey];
            const oldIndex = items.findIndex(t => t.id === activeId);

            // If dropped on the column background (isOverColumn), move to end?
            // Or if using SortableContext with same items, dnd-kit usually gives correct new index relative to items?
            // But if `over` is the container ID, `newIndex` via `items.findIndex` logic (overId) fails because overId is container.

            if (isOverColumn) {
                // Dropped on the container itself (empty space).
                // If dragging down to bottom, usually we want to append.
                // If oldIndex is -1 (shouldn't happen), ignore.
                if (oldIndex !== -1) {
                    // Move to end if it's not already last?
                    // Actually, if we drop on container, dnd-kit doesn't tell us "where" in container unless we use custom collision or calculation.
                    // But typically dropping on container implies append.
                    if (oldIndex !== items.length - 1) {
                        const newItems = arrayMove(items, oldIndex, items.length - 1);
                        const updates = newItems.map((t, idx) => ({
                            id: t.id,
                            sortOrder: idx
                        }));
                        reorderTasks(updates);
                    }
                }
            } else {
                // Dropped on another task
                const newIndex = items.findIndex(t => t.id === overId);
                if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                    const newItems = arrayMove(items, oldIndex, newIndex);
                    const updates = newItems.map((t, idx) => ({
                        id: t.id,
                        sortOrder: idx,
                    }));
                    reorderTasks(updates);
                }
            }
        } else {
            // Move to different status
            const destStatus = taskStatuses.find(s => s.id === targetStatusIdForDb);
            const updates = {
                statusId: targetStatusIdForDb,
                isCompleted: destStatus ? destStatus.is_completed : false
            };
            updateTask(activeId, updates);
        }
    };

    const handleCreateTaskInStatus = async (e, statusId) => {
        e.preventDefault();
        const content = newTaskContent[statusId];
        if (!content || !content.trim()) return;

        await createTask({
            projectId,
            statusId,
            content
        });
        setNewTaskContent(prev => ({ ...prev, [statusId]: "" }));
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="d-flex gap-3 h-100 overflow-x-auto pb-3">
                {taskStatuses.map(status => (
                    <KanbanColumn
                        key={status.id}
                        status={status}
                        tasks={tasksByStatus[status.id] || []}
                        onAdd={handleCreateTaskInStatus}
                        newTaskContent={newTaskContent}
                        setNewTaskContent={setNewTaskContent}
                        isPro={isPro}
                        onRequestCreateTask={onRequestCreateTask}
                    >
                        {(tasksByStatus[status.id] || []).map(task => (
                            <SortableTaskCard
                                key={task.id}
                                task={task}
                                onCreateSubtask={onCreateSubtask}
                                onDeleteTask={onDeleteTask}
                                allTags={allTags}
                                onAssignTag={onAssignTag}
                                onRemoveTag={onRemoveTag}
                                onAddReminder={onAddReminder}
                                onDeleteReminder={onDeleteReminder}
                                onUpdateTask={updateTask}
                                taskStatuses={taskStatuses}
                                members={members}
                            />
                        ))}
                    </KanbanColumn>
                ))}

                {/* Uncategorized / No Status Column */}
                {(tasksByStatus['uncategorized']?.length > 0 || taskStatuses.length === 0) && (
                    <KanbanColumn
                        key="uncategorized"
                        status={{ id: 'uncategorized', name: 'Tasks' }}
                        tasks={tasksByStatus['uncategorized'] || []}
                        onAdd={() => { }} // No quick add for uncategorized
                        newTaskContent={{}}
                        setNewTaskContent={() => { }}
                    >
                        {(tasksByStatus['uncategorized'] || []).map(task => (
                            <SortableTaskCard
                                key={task.id}
                                task={task}
                                onCreateSubtask={onCreateSubtask}
                                onDeleteTask={onDeleteTask}
                                allTags={allTags}
                                onAssignTag={onAssignTag}
                                onRemoveTag={onRemoveTag}
                                onAddReminder={onAddReminder}
                                onDeleteReminder={onDeleteReminder}
                                onUpdateTask={updateTask}
                                taskStatuses={taskStatuses}
                                members={members}
                            />
                        ))}
                    </KanbanColumn>
                )}
            </div>

            <DragOverlay>
                {activeId ? (
                    <div style={{ transform: 'scale(1.05)', cursor: 'grabbing', width: '280px' }}>
                        <TaskCard
                            task={tasks.find(t => t.id === activeId)}
                            completed={tasks.find(t => t.id === activeId)?.is_completed}
                            onCreateSubtask={() => { }}
                            onAddReminder={onAddReminder}
                            onDeleteReminder={onDeleteReminder}
                            allTags={allTags}
                            onUpdateTask={updateTask}
                            taskStatuses={taskStatuses}
                            members={members}
                        />
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};
export default KanbanBoard;
